import { use, useContext, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';

import CentredSpinner from '../../../Components/CentredSpinner';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { UserPreferencesContext } from '../../../Contexts/UserPreferencesContext';
import CheckBoxFilter from '../../../DataTable/datatable.checkboxfilter';
import DateColumn from '../../../DataTable/datatable.datecolumn';
import ExpandingTable from '../../../DataTable/datatable.expandingtable';
import NoFilter from '../../../DataTable/datatable.nofilter';
import { Genealogy } from '../../../Types/Genealogy';

const GenealogySection = memo(function GenealogySection() {
    const { lotDetail: { summary: { lotNumber }, genealogy }, loadingGenealogy, getLotGenealogy } = useContext(LotDetailContext);

    const { userPreferences, setIncludeIndirectTransfers } = use(UserPreferencesContext);

    const [includeIndirect, setIncludeIndirect] = useState(userPreferences?.lotDetail?.genealogy?.includeIndirectTransfers);
    const [filteredGenealogy, setFilteredGenealogy] = useState<Genealogy[]>([]);

    const { t } = useTranslation();

    useEffect(() => {
        getLotGenealogy();
    }, [getLotGenealogy]);

    useEffect(() => {
        if (genealogy) {
            if (includeIndirect) {
                setFilteredGenealogy(genealogy);
            } else {
                setFilteredGenealogy(genealogy.filter(g => g.sourceLot === lotNumber || g.destinationLot === lotNumber));
            }
        }
    }, [includeIndirect, lotNumber, genealogy]);

    useEffect(() => {
        if (userPreferences?.lotDetail?.genealogy?.includeIndirectTransfers) {
            setIncludeIndirect(userPreferences?.lotDetail?.genealogy?.includeIndirectTransfers);
        }
    }, [userPreferences?.lotDetail?.genealogy]);

    useEffect(() => {
        if (includeIndirect && includeIndirect !== userPreferences?.lotDetail?.genealogy?.includeIndirectTransfers) {
            setIncludeIndirectTransfers(includeIndirect);
        }
    }, [includeIndirect]);

    const tableColumns = [
        {
            // Build our expander column
            id: 'expander', // Make sure it has an ID
            Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }: any) => (
                <span {...getToggleAllRowsExpandedProps()}>
                    {isAllRowsExpanded ? '-' : '+'}
                </span>
            ),
            Cell: ({ row }: any) =>
                // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
                // to build the toggle for expanding a row
                row.canExpand ? (
                    <span
                        {...row.getToggleRowExpandedProps({
                            style: {
                                // We can even use the row.depth property
                                // and paddingLeft to indicate the depth
                                // of the row
                                paddingLeft: `${row.depth * 2}rem`,
                            },
                        })}
                    >
                        {row.isExpanded ? '-' : '+'}
                    </span>
                ) : null,
        },
        {
            Header: t('LotDetails.Genealogy.Transfer'),
            accessor: 'transferID',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.Genealogy.Grade'),
            accessor: 'grade',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.Genealogy.SourceLot'),
            accessor: 'sourceLot',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.Genealogy.DestinationLot'),
            accessor: 'destinationLot',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.Genealogy.Weight'),
            accessor: 'weight',
            Filter: NoFilter
        },
        {
            Header: t('LotDetails.Genealogy.SourceEquipment'),
            accessor: 'sourceEquipment',
            filter: 'includesString'
        },
        {
            Header: t('LotDetails.Genealogy.DestinationEquipment'),
            accessor: 'destinationEquipment',
            filter: 'includesString'
        },
        DateColumn({ header: t('LotDetails.Genealogy.StartTime'), accessor: 'startTimeUtc' }),
        DateColumn({ header: t('LotDetails.Genealogy.EndTime'), accessor: 'endTimeUtc' })
    ];

    const initialState = {
        sortBy: [
            {
                id: 'startTimeUtc',
                desc: false
            },
            {
                id: 'endTimeUtc',
                desc: false
            }
        ]
    };

    if (loadingGenealogy) {
        return <CentredSpinner />;
    }

    return genealogy && genealogy.length > 0 ?
        <ExpandingTable columns={tableColumns} data={filteredGenealogy} initialState={initialState} >
            <CheckBoxFilter
                checked={includeIndirect}
                setChecked={setIncludeIndirect}
                name={includeIndirect ? t('LotDetails.Genealogy.HideIndirectTransers') : t('LotDetails.Genealogy.IncludeIndirectTransers')} />
        </ExpandingTable>
        : <span>{t<string>('CommonResponses.NoData')}</span>;
});

export default GenealogySection;